<template>
  <Popper
    trigger="clickToToggle"
    :disabled="disabled"
    @show="extended = true"
    @hide="extended = false"
    class="w-fit block"
    ref="popper"
    :options="{
      placement: dropdownIsOnRightHandSide ? 'bottom-end' : 'bottom-start',
      positionFixed: true,
      modifiers: {
        // Disabling preventOverflow stops the options from following the scroll.
        preventOverflow: {
          enabled: false,
        },
        // Disabling hide explicitly stops preventOverflow:false from spamming the console with warnings.
        hide: {
          enabled: false,
        },
      },
    }"
  >
    <button
      :disabled="disabled"
      @keyup.esc.prevent="closeDropdown"
      @keyup.down.prevent="openDropdown"
      ref="dropdownButton"
      slot="reference"
      data-test="reference"
      class="font-body w-fit py-sm px-twenty text-signableBrandColours-black text-12 flex gap-sm items-center rounded-5 outline-0 border border-solid border-disabledGrey bg-signableBrandColours-white"
      :class="{
        [buttonClass]: true,
        'bg-signableBrandColours-lightGrey': extended,
        'cursor-pointer hover:bg-offWhite focus-visible:bg-offWhite active:bg-signableBrandColours-white':
          !disabled,
        'cursor-not-allowed opacity-70': disabled,
      }"
    >
      {{ text }}
      <SolidDownTriangleIcon
        class="transition-all duration-200"
        :class="{ 'rotate-180': extended }"
        data-test="icon"
      />
    </button>
    <div
      class="popper !m-0 !p-0 !shadow-baseDropdown flex flex-col rounded-5 border border-solid border-disabledGrey bg-signableBrandColours-white divide-y divide-disabledGrey overflow-hidden"
      data-test="options"
    >
      <BaseOption
        v-for="(option, index) in value"
        v-on="$listeners"
        @selected="closeDropdown"
        @down="handleDownKey(index)"
        @up="handleUpKey(index)"
        @esc="closeDropdown"
        ref="options"
        :key="index"
        :action="option.action"
        :text="option.text"
        :disabled="option.disabled"
      />
    </div>
  </Popper>
</template>

<script>
// @ts-check
import { defineComponent, ref } from 'vue';
import Popper from 'vue-popperjs';

import 'vue-popperjs/dist/vue-popper.css';

import { BaseOption } from '@/components/library/dropdowns/BaseOption';
import { SolidDownTriangleIcon } from '@/components/library/icons';

/** @typedef {import('@/components/library/dropdowns/BaseOption').TOption} TOption */

export default defineComponent({
  components: { BaseOption, SolidDownTriangleIcon, Popper },
  props: {
    text: {
      type: String,
      required: true,
    },
    /**
     * value contains objects that have 'action', 'text' and 'disabled' keys
     * @type {import('vue').PropOptions<TOption[]>}
     */
    value: {
      type: Array,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    buttonClass: {
      type: String,
      default: '',
    },
    dropdownIsOnRightHandSide: {
      // Is the dropdown on the right hand side of its parent?
      // Controls which side the options jut out to.
      // Setting Right as default may seem counter-intuitive to non-Japanese people,
      // But it looks like in current designs that's where it will mostly be used
      type: Boolean,
      default: true,
    },
  },
  setup() {
    const popper = ref(null);
    const dropdownButton = ref(null);
    const extended = ref(null);
    const options = ref(null);

    function closeDropdown() {
      popper.value.doClose();
      dropdownButton.value.focus();
    }

    function getPrevViableOption(currentIndex) {
      return options.value.findLast((element, targetIndex) => targetIndex < currentIndex && !element.disabled);
    }

    function getNextViableOption(currentIndex) {
      return options.value.find((element, targetIndex) => targetIndex > currentIndex && !element.disabled);
    }

    function openDropdown() {
      if (!extended.value) {
        popper.value.doShow();
      } else {
        const targetOption = getNextViableOption(-1);
        if (targetOption) {
          targetOption.$el.focus();
        }
      }
    }

    function handleDownKey(index) {
      const targetOption = getNextViableOption(index);
      if (targetOption) {
        targetOption.$el.focus();
      }
    }

    function handleUpKey(index) {
      const targetOption = getPrevViableOption(index);
      if (targetOption) {
        targetOption.$el.focus();
      } else {
        closeDropdown();
        dropdownButton.value.focus();
      }
    }

    return {
      popper,
      dropdownButton,
      extended,
      options,
      openDropdown,
      closeDropdown,
      handleDownKey,
      handleUpKey,
    };
  },
});
</script>

